import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, ExternalLink, Radio, GraduationCap, ChevronLeft, ChevronRight, ArrowUpRight, CalendarDays } from 'lucide-react';
import api from '../lib/api';
import { Spinner } from '../components/ui/Spinner';

const DAY_ABBR = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const WEEKS_AHEAD = 4;

const typeMeta = {
  lesson: { icon: GraduationCap, label: 'Lesson' },
  live_trading: { icon: Radio, label: 'Live Trading' },
  live: { icon: Radio, label: 'Live' },
};

const startOfSunday = (date) => { const d = new Date(date); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() - d.getDay()); return d; };
const ymdLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
const fmtTime = (iso) => { try { return new Date(iso).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' }); } catch { return ''; } };
function fmtRange(ws) {
  const we = addDays(ws, 6);
  const same = ws.getMonth() === we.getMonth();
  return `${ws.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${we.toLocaleDateString(undefined, same ? { day: 'numeric' } : { month: 'short', day: 'numeric' })}, ${we.getFullYear()}`;
}

function EventCard({ e, onClick }) {
  const meta = typeMeta[(e.event_type || '').toLowerCase()] || { icon: Calendar, label: e.event_type || 'Event' };
  const Icon = meta.icon;
  const type = (e.event_type || '').toLowerCase();
  const isLive = type.includes('live');
  const isLesson = type === 'lesson';
  const c = isLesson
    ? { bar: 'bg-purple-500/70', icon: 'bg-purple-500/15 text-purple-300', badge: 'border-purple-500/30 bg-purple-500/15 text-purple-300', hover: 'hover:border-purple-500/50', link: 'text-purple-300' }
    : { bar: 'bg-emerald/70', icon: 'bg-emerald/12 text-emerald', badge: 'border-emerald/30 bg-emerald/10 text-emerald', hover: 'hover:border-emerald/50', link: 'text-emerald' };
  const link = e.external_url;
  const clickable = link || e.lesson_id;
  const now = Date.now();
  const st = e.start_time ? new Date(e.start_time).getTime() : 0;
  const en = e.end_time ? new Date(e.end_time).getTime() : st + 2 * 3600 * 1000;
  const liveNow = isLive && now >= st - 5 * 60 * 1000 && now <= en;
  return (
    <div
      onClick={onClick}
      data-testid={`schedule-event-${e.occurrence_id || e.event_id}`}
      className={`group relative overflow-hidden rounded-xl border border-white/10 bg-gradient-to-br from-white/[0.04] to-transparent p-3.5 transition-all ${clickable ? `cursor-pointer hover:-translate-y-0.5 ${c.hover}` : ''}`}
    >
      <div className={`absolute inset-y-0 left-0 w-1 ${c.bar}`} />
      <div className="flex items-start gap-3 pl-1.5">
        <div className={`relative grid h-10 w-10 shrink-0 place-items-center rounded-xl ${c.icon}`}>
          <Icon size={18} />
          {liveNow && <span className="absolute -right-0.5 -top-0.5 h-2.5 w-2.5 animate-ping rounded-full bg-emerald" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="truncate font-heading text-sm font-semibold text-white">{e.title}</h3>
            <span className={`rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${c.badge}`}>{meta.label}</span>
            {liveNow && <span className="flex items-center gap-1 rounded-full bg-emerald px-2 py-0.5 text-[10px] font-bold uppercase text-black"><span className="h-1.5 w-1.5 rounded-full bg-black" /> Live now</span>}
          </div>
          {e.description && <p className="mt-0.5 line-clamp-1 text-xs text-zinc-400">{e.description}</p>}
          <div className="mt-1.5 flex flex-wrap items-center gap-3 text-[11px] text-zinc-500">
            {e.start_time && <span className="tnum flex items-center gap-1 rounded-md bg-white/5 px-1.5 py-0.5 text-zinc-300"><Clock size={11} /> {fmtTime(e.start_time)}{e.end_time ? ` – ${fmtTime(e.end_time)}` : ''}</span>}
            {link && <span className={`flex items-center gap-1 font-medium ${c.link}`}><ExternalLink size={11} /> Join</span>}
            {!link && e.lesson_id && <span className={`flex items-center gap-1 font-medium ${c.link}`}><ArrowUpRight size={11} /> Watch</span>}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Schedule() {
  const navigate = useNavigate();
  const [weekStart, setWeekStart] = useState(() => startOfSunday(new Date()));
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const minWeek = startOfSunday(new Date());
  const maxWeek = addDays(minWeek, WEEKS_AHEAD * 7);
  const canPrev = ymdLocal(weekStart) > ymdLocal(minWeek);
  const canNext = ymdLocal(weekStart) < ymdLocal(maxWeek);
  const todayYmd = ymdLocal(new Date());

  const fetchWeek = useCallback((ws) => {
    setLoading(true);
    api.get('/schedule', { params: { week_start: ymdLocal(ws) } })
      .then(({ data }) => setEvents(data.events || []))
      .catch(() => setEvents([]))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { fetchWeek(weekStart); }, [weekStart, fetchWeek]);

  const go = (dir) => { if ((dir < 0 && !canPrev) || (dir > 0 && !canNext)) return; setWeekStart((w) => addDays(w, dir * 7)); };

  const byDay = useMemo(() => {
    const m = {};
    events.forEach((e) => { const k = e.occurrence_date || (e.start_time || '').slice(0, 10); (m[k] = m[k] || []).push(e); });
    Object.values(m).forEach((l) => l.sort((a, b) => (a.start_time || '').localeCompare(b.start_time || '')));
    return m;
  }, [events]);

  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  const openEvent = (e) => { if (e.external_url) window.open(e.external_url, '_blank', 'noreferrer'); else if (e.lesson_id) navigate(`/app/lessons/${e.lesson_id}`); };

  return (
    <div className="space-y-6" data-testid="schedule-page">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-widest text-emerald">Schedule</p>
        <h1 className="font-heading text-3xl font-bold tracking-tight text-white">This week</h1>
        <p className="mt-1 text-sm text-zinc-500">Live trading sessions and lessons, Sunday to Saturday.</p>
      </div>

      {/* Navigator + day strip */}
      <div className="grain relative overflow-hidden rounded-2xl border border-white/10 bg-ink-card p-4">
        <div className="pointer-events-none absolute -right-10 -top-16 h-40 w-40 rounded-full bg-emerald/10 blur-3xl" />
        <div className="relative flex items-center justify-between">
          <button onClick={() => go(-1)} disabled={!canPrev} data-testid="schedule-prev-week"
            className="grid h-10 w-10 place-items-center rounded-xl border border-white/10 text-zinc-300 transition-colors hover:border-emerald/50 hover:text-emerald disabled:opacity-30">
            <ChevronLeft size={18} />
          </button>
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 font-heading text-base font-semibold text-white"><Calendar size={15} className="text-emerald" /> {fmtRange(weekStart)}</div>
            {ymdLocal(weekStart) !== ymdLocal(minWeek) && (
              <button onClick={() => setWeekStart(startOfSunday(new Date()))} data-testid="schedule-this-week" className="mt-0.5 text-xs text-emerald hover:underline">Back to this week</button>
            )}
          </div>
          <button onClick={() => go(1)} disabled={!canNext} data-testid="schedule-next-week"
            className="grid h-10 w-10 place-items-center rounded-xl border border-white/10 text-zinc-300 transition-colors hover:border-emerald/50 hover:text-emerald disabled:opacity-30">
            <ChevronRight size={18} />
          </button>
        </div>
        <div className="relative mt-4 grid grid-cols-7 gap-1.5">
          {days.map((day) => {
            const k = ymdLocal(day);
            const count = (byDay[k] || []).length;
            const isToday = k === todayYmd;
            return (
              <div key={k} className={`flex flex-col items-center rounded-xl border py-2 transition-colors ${isToday ? 'border-emerald bg-emerald/15' : count ? 'border-white/10 bg-white/[0.03]' : 'border-transparent'}`}>
                <span className={`text-[10px] font-semibold uppercase ${isToday ? 'text-emerald' : 'text-zinc-500'}`}>{DAY_ABBR[day.getDay()]}</span>
                <span className={`font-heading text-lg font-bold leading-tight ${isToday ? 'text-emerald' : count ? 'text-white' : 'text-zinc-600'}`}>{day.getDate()}</span>
                <span className={`mt-1 h-1.5 w-1.5 rounded-full ${count ? 'bg-emerald' : 'bg-transparent'}`} />
              </div>
            );
          })}
        </div>
      </div>

      {loading ? (
        <div className="grid h-[35vh] place-items-center"><Spinner className="h-8 w-8" /></div>
      ) : (
        <>
          <div className="space-y-2.5">
            {days.map((day, di) => {
              const k = ymdLocal(day);
              const list = byDay[k] || [];
              const isToday = k === todayYmd;
              if (list.length === 0) {
                return (
                  <div key={k} className={`flex items-center gap-4 rounded-xl px-4 py-2.5 ${isToday ? 'bg-emerald/5' : ''}`} data-testid={`schedule-day-${k}`}>
                    <div className="flex w-12 shrink-0 items-baseline gap-1.5">
                      <span className={`text-[10px] font-semibold uppercase ${isToday ? 'text-emerald' : 'text-zinc-600'}`}>{DAY_ABBR[day.getDay()]}</span>
                      <span className={`font-heading text-sm font-bold ${isToday ? 'text-emerald' : 'text-zinc-600'}`}>{day.getDate()}</span>
                    </div>
                    <span className="text-xs text-zinc-600">No sessions{isToday ? ' today' : ''}</span>
                  </div>
                );
              }
              return (
                <motion.div key={k} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: di * 0.03 }}
                  className={`flex gap-4 rounded-2xl border p-4 ${isToday ? 'border-emerald/40 bg-emerald/[0.05]' : 'border-white/5 bg-ink-card'}`} data-testid={`schedule-day-${k}`}>
                  <div className="flex w-14 shrink-0 flex-col items-center">
                    <span className={`text-[11px] font-semibold uppercase tracking-wide ${isToday ? 'text-emerald' : 'text-zinc-500'}`}>{DAY_ABBR[day.getDay()]}</span>
                    <span className={`font-heading text-3xl font-extrabold leading-none ${isToday ? 'text-emerald' : 'text-white'}`}>{day.getDate()}</span>
                    {isToday && <span className="mt-1.5 rounded-full bg-emerald px-1.5 py-0.5 text-[9px] font-bold uppercase text-black">Today</span>}
                  </div>
                  <div className="min-w-0 flex-1 space-y-2">
                    {list.map((e) => <EventCard key={e.occurrence_id || e.event_id} e={e} onClick={() => openEvent(e)} />)}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
